import { Sparkles, FolderInput, Copy, Trash2, X } from 'lucide-react'
import { useLocale } from '../i18n/context'
import { DECK_TOP_STRIP_CLEARANCE } from './DeckTopStrip'

interface Props {
  /**
   * How many cards are selected, or `null` when that is not yet known.
   *
   * `null` is a real state, not a missing value to be papered over with a
   * loaded-set number: a whole-deck selection's size comes from the server, and
   * until it arrives this bar says so. Printing the count of what happened to
   * be fetched is exactly the defect this surface carried (brief §23 item 9) —
   * the reader deleted 30 cards of 93 while the counter agreed they had
   * selected everything.
   */
  selectedCount: number | null
  /** What `Select all` would select — the deck's count for the active filter,
   * or `null` while unknown. */
  totalSelectable: number | null
  onExit: () => void
  onSelectAll: () => void
  onFill: () => void
  onMove: () => void
  /** Duplicate the selection into another deck, leaving this one as it is
   * (brief §23 item 10). Desktop has had this since the bulk bar existed;
   * without it here a card could be relocated but never duplicated, so the
   * only way to get a word into a second deck was to take it out of the
   * first. */
  onCopy: () => void
  onDelete: () => void
  fillingProgress: { done: number; total: number } | null
  /** Fill is resolving the selection server-side before it starts. */
  resolving?: boolean
  /** Why an action cannot be honestly performed right now. Shown in place of
   * the buttons rather than beside them — a refusal that leaves the button
   * tappable is not a refusal. */
  notice?: string | null
}

// Select-mode chrome (Prompt 8 frame "Long-press select"; repositioned onto
// the pinned strips themselves for Prompt B). Two pieces, each one taking
// over the pinned bar it replaces rather than sitting alongside it: a header
// that occupies the top strip's exact slot (count + Select all + exit) and
// an action bar that occupies the bottom bar's slot for as long as selection
// is active. Body only — geometry/position is the caller's (DeckMobileFeed),
// same convention as DeckViewBar.
export function SelectModeHeader({
  selectedCount, totalSelectable, onExit, onSelectAll, selectAllDisabledReason,
}: Pick<Props, 'selectedCount' | 'totalSelectable' | 'onExit' | 'onSelectAll'> & {
  /** Set when `Select all` cannot mean the whole deck — e.g. a search is
   * narrowing the feed to a set the selection cannot name. The reason replaces
   * the control, so nothing offers an action it would perform incorrectly. */
  selectAllDisabledReason?: string | null
}) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  const dm = tr.deckManagement
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        height: DECK_TOP_STRIP_CLEARANCE,
        paddingTop: 'env(safe-area-inset-top, 0px)', paddingRight: 4,
        // Selected-mode plane (DESIGN_SYSTEM.md Wave 4 §7) — opaque, distinct
        // from --bg-chrome, so the mode reads at a glance in either theme.
        background: 'var(--bg-chrome-select)',
        borderBottom: '1px solid var(--border-chrome-select)',
      }}
    >
      <button
        onClick={onExit}
        aria-label={dm.exitSelectAria}
        style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 44, height: 44,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-primary)', padding: 0,
        }}
      >
        <X size={21} />
      </button>
      <span
        style={{
          flex: 1, minWidth: 0,
          fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 15,
          color: 'var(--text-primary)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {selectedCount === null ? dd.nCardsSelectedPending : dd.nCardsSelected(selectedCount)}
      </span>
      {selectAllDisabledReason ? (
        <span
          style={{
            flexShrink: 1, minWidth: 0, marginRight: 8,
            fontFamily: 'var(--font-ui)', fontSize: 11.5,
            color: 'var(--text-secondary)', textAlign: 'right',
          }}
        >
          {selectAllDisabledReason}
        </span>
      ) : (
        <button
          onClick={onSelectAll}
          style={{ flexShrink: 0, marginRight: 8, background: 'transparent', border: 'none', color: 'var(--accent)', fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }}
        >
          {totalSelectable === null ? dd.selectAllPending : dd.selectAll(totalSelectable)}
        </button>
      )}
    </div>
  )
}

export default function SelectModeActionBar({
  onFill, onMove, onCopy, onDelete, fillingProgress, resolving, notice,
}: Props) {
  const { tr } = useLocale()
  const dm = tr.deckManagement

  // Three states share one slot and are mutually exclusive, in this order:
  // a refusal outranks progress, and progress outranks the buttons. The bar
  // never shows an action alongside a reason that action cannot be performed.
  if (notice) {
    return (
      <div style={statusSlotStyle}>
        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--danger)' }}>
          {notice}
        </span>
      </div>
    )
  }

  if (resolving || fillingProgress) {
    return (
      <div style={statusSlotStyle}>
        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>
          {fillingProgress
            ? dm.fillingSelected(fillingProgress.done, fillingProgress.total)
            : dm.fillResolving}
        </span>
      </div>
    )
  }

  // Four actions, in the desktop bar's order (Fill, Move, Copy, Delete) so the
  // same set reads the same way on both surfaces.
  //
  // Move and Copy are adjacent and do different things to this deck, so the
  // pair carries the distinction rather than relying on the reader's memory of
  // which word means which:
  //  - unrelated icon shapes — a folder taking something IN versus two stacked
  //    sheets, not two variants of one glyph;
  //  - different weight — Move keeps the accent, Copy is neutral, exactly the
  //    filled/outlined split the desktop bar already uses for this pair;
  //  - and the picker that follows states the consequence for THIS deck in a
  //    sentence, which is the last point before anything happens.
  return (
    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'stretch', padding: '11px 4px calc(14px + env(safe-area-inset-bottom, 0px))', background: 'var(--bg-chrome-select)', borderTop: '1px solid var(--border-chrome-select)', boxShadow: 'var(--shadow-chrome-above)' }}>
      <button onClick={onFill} style={actionBtnStyle('var(--accent)')}>
        <Sparkles size={20} strokeWidth={1.9} />
        <span className="cs-select-action-label">{dm.fillSelectedBtn}</span>
      </button>
      <button onClick={onMove} style={actionBtnStyle('var(--accent)')}>
        <FolderInput size={20} strokeWidth={1.9} />
        <span className="cs-select-action-label">{dm.moveSelectedBtn}</span>
      </button>
      <button onClick={onCopy} style={actionBtnStyle('var(--text-primary)')}>
        <Copy size={20} strokeWidth={1.9} />
        <span className="cs-select-action-label">{dm.copySelectedBtn}</span>
      </button>
      <button onClick={onDelete} style={actionBtnStyle('var(--danger)')}>
        <Trash2 size={20} strokeWidth={1.9} />
        <span className="cs-select-action-label">{dm.deleteSelectedBtn}</span>
      </button>
    </div>
  )
}

// How the fourth action fits at narrow widths is a decision, not an overflow —
// see `.cs-select-action-label` in index.css for the rule and for the
// alternatives it rules out.

const statusSlotStyle = {
  flexShrink: 0,
  padding: '14px 16px calc(14px + env(safe-area-inset-bottom, 0px))',
  background: 'var(--bg-chrome-select)',
  borderTop: '1px solid var(--border-chrome-select)',
  boxShadow: 'var(--shadow-chrome-above)',
  textAlign: 'center',
} as const

function actionBtnStyle(color: string) {
  return {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: 4,
    background: 'transparent', border: 'none', cursor: 'pointer',
    // Equal cells that may shrink below their content — without `minWidth: 0`
    // a flex item refuses to go under its intrinsic width and the fourth
    // action pushes the row wide instead of sharing it.
    //
    // Longhands rather than the `flex` shorthand: jsdom's CSSOM drops the
    // shorthand outright, so the rule would be invisible to any test that
    // tried to hold it in place.
    flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0, padding: '0 2px',
    color, fontFamily: 'var(--font-ui)', fontWeight: 600,
  } as const
}
